import "core-js/modules/es.array.concat";
import "core-js/modules/es.object.keys";
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { pickHTMLProps } from 'pick-react-known-prop';
import { makeStyles } from '@material-ui/core/styles';
import useTranslate from '@sprinx/react-globalize/useTranslate';
import ProductImageImageComponent from './ProductImageImageComponent';
import ProductImageVideoComponent from './ProductImageVideoComponent';
import ProductImageIframeComponent from './ProductImageIframeComponent';
var useStyles = makeStyles(function (theme) {
  return {
    root: {},
    image: {
      objectFit: 'contain'
    },
    'size-mini-square': {
      height: theme.spacing(4),
      width: theme.spacing(4)
    },
    'size-mini-landscape': {
      height: theme.spacing(4) * 3 / 4,
      width: theme.spacing(4)
    },
    'size-mini-portrait': {
      height: theme.spacing(4),
      width: theme.spacing(4) * 4 / 3
    },
    'size-mini-wide': {
      height: theme.spacing(4) * 9 / 16,
      width: theme.spacing(4)
    },
    'size-mini-tall': {
      height: theme.spacing(4),
      width: theme.spacing(4) * 16 / 9
    },
    'size-small-square': {
      height: theme.spacing(8),
      width: theme.spacing(8)
    },
    'size-small-landscape': {
      height: theme.spacing(8) * 3 / 4,
      width: theme.spacing(8)
    },
    'size-small-portrait': {
      height: theme.spacing(8),
      width: theme.spacing(8) * 4 / 3
    },
    'size-small-wide': {
      height: theme.spacing(8) * 9 / 16,
      width: theme.spacing(8)
    },
    'size-small-tall': {
      height: theme.spacing(8),
      width: theme.spacing(8) * 16 / 9
    },
    'size-normal-square': {
      height: theme.spacing(16),
      width: theme.spacing(16)
    },
    'size-normal-landscape': {
      height: theme.spacing(16) * 3 / 4,
      width: theme.spacing(16)
    },
    'size-normal-portrait': {
      height: theme.spacing(16),
      width: theme.spacing(16) * 4 / 3
    },
    'size-normal-wide': {
      height: theme.spacing(16) * 9 / 16,
      width: theme.spacing(16)
    },
    'size-normal-tall': {
      height: theme.spacing(16),
      width: theme.spacing(16) * 16 / 9
    },
    'size-big-square': {
      height: theme.spacing(32),
      width: theme.spacing(32)
    },
    'size-big-landscape': {
      height: theme.spacing(32) * 3 / 4,
      width: theme.spacing(32)
    },
    'size-big-portrait': {
      height: theme.spacing(32),
      width: theme.spacing(32) * 4 / 3
    },
    'size-big-wide': {
      height: theme.spacing(32) * 9 / 16,
      width: theme.spacing(32)
    },
    'size-big-tall': {
      height: theme.spacing(32),
      width: theme.spacing(32) * 16 / 9
    }
  };
}, {
  name: 'SxComProductImage'
});
var defaultMediaSizeMap = {
  mini: 'mini',
  small: 'mini',
  normal: 'small',
  big: 'small'
};
var mediaTypeComponents = {
  image: ProductImageImageComponent,
  iframe: ProductImageIframeComponent,
  video: ProductImageVideoComponent
};

function ProductImage(_ref) {
  var active = _ref.active,
      alt = _ref.alt,
      pClasses = _ref.classes,
      className = _ref.className,
      mediaSizeMap = _ref.mediaSizeMap,
      pMediaTypeComponents = _ref.mediaTypeComponents,
      shape = _ref.shape,
      shrink = _ref.shrink,
      size = _ref.size,
      src = _ref.src,
      stream = _ref.stream,
      type = _ref.type,
      rest = _objectWithoutProperties(_ref, ["active", "alt", "classes", "className", "mediaSizeMap", "mediaTypeComponents", "shape", "shrink", "size", "src", "stream", "type"]);

  var mediaType = type || 'image';
  var classes = useStyles({
    classes: pClasses
  });

  var Component = _extends({}, mediaTypeComponents, pMediaTypeComponents)[mediaType];

  var t = useTranslate();

  if (!Component) {
    // eslint-disable-next-line
    console.warn("WARNING: a component for media type `".concat(mediaType, "` not defined."));
    return null;
  }

  return /*#__PURE__*/React.createElement("div", _extends({}, pickHTMLProps(rest), {
    className: clsx(classes.root, classes["size-".concat(size, "-").concat(shape)], className)
  }), /*#__PURE__*/React.createElement(Component, {
    className: clsx(classes.image, classes["size-".concat(size, "-").concat(shape)]),
    alt: alt ? t(alt, {
      acceptString: true
    }) : '',
    src: src,
    stream: stream,
    active: active,
    shape: shape,
    shrink: shrink,
    size: size,
    mediaSizeMap: _extends({}, defaultMediaSizeMap, mediaSizeMap)
  }));
}

ProductImage.propTypes = {
  active: PropTypes.bool,
  alt: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.shape({
    language: PropTypes.string.isRequired,
    text: PropTypes.string
  }))]),
  classes: PropTypes.shape({
    image: PropTypes.string,
    root: PropTypes.string,
    'size-big-landscape': PropTypes.string,
    'size-big-portrait': PropTypes.string,
    'size-big-square': PropTypes.string,
    'size-big-tall': PropTypes.string,
    'size-big-wide': PropTypes.string,
    'size-mini-landscape': PropTypes.string,
    'size-mini-portrait': PropTypes.string,
    'size-mini-square': PropTypes.string,
    'size-mini-tall': PropTypes.string,
    'size-mini-wide': PropTypes.string,
    'size-normal-landscape': PropTypes.string,
    'size-normal-portrait': PropTypes.string,
    'size-normal-square': PropTypes.string,
    'size-normal-tall': PropTypes.string,
    'size-normal-wide': PropTypes.string,
    'size-small-landscape': PropTypes.string,
    'size-small-portrait': PropTypes.string,
    'size-small-square': PropTypes.string,
    'size-small-tall': PropTypes.string,
    'size-small-wide': PropTypes.string
  }),
  className: PropTypes.string,
  mediaSizeMap: PropTypes.shape({}),
  mediaTypeComponents: PropTypes.shape({}),
  shape: PropTypes.oneOf(['square', 'landscape', 'portrait', 'wide', 'tall']),
  shrink: PropTypes.oneOf(['crop', 'fit']),
  size: PropTypes.oneOf(['mini', 'small', 'normal', 'big']),
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string))]),
  stream: PropTypes.string,
  type: PropTypes.oneOfType([PropTypes.oneOf(Object.keys(mediaTypeComponents)), PropTypes.string])
};
ProductImage.defaultProps = {
  active: false,
  alt: undefined,
  classes: undefined,
  className: undefined,
  mediaSizeMap: undefined,
  mediaTypeComponents: undefined,
  shape: 'landscape',
  shrink: 'fit',
  size: 'normal',
  src: undefined,
  stream: undefined,
  type: 'image'
};
export default ProductImage;